import React, { useState, useMemo, useEffect } from 'react';
import { getSecurityServicesData } from '../../utils/securityServicesDataManager';
import SecurityServicesHeader from './SecurityServicesHeader';
import SecurityServicesTable from './SecurityServicesTable';
import SecurityServicesFilters from './SecurityServicesFilters';

/**
 * SecurityServicesMatrix - Main component for the Security Services Matrix
 * Displays a comprehensive comparison matrix of security services and their capabilities
 */
const SecurityServicesMatrix = () => {
  // State for data (will be updated when localStorage changes)
  const [securityServicesData, setSecurityServicesData] = useState(getSecurityServicesData());
  
  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceCategory, setSelectedServiceCategory] = useState('all');
  const [selectedFeatureCategory, setSelectedFeatureCategory] = useState('all');
  const [viewMode, setViewMode] = useState('matrix'); // 'matrix' or 'cards'

  // Listen for localStorage changes to reload data
  useEffect(() => {
    const handleStorageChange = () => {
      setSecurityServicesData(getSecurityServicesData());
    };

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom event (changes from same tab)
    window.addEventListener('securityServicesDataUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('securityServicesDataUpdated', handleStorageChange);
    };
  }, []);

  // Refresh data when component mounts (in case of navigation from admin)
  useEffect(() => {
    setSecurityServicesData(getSecurityServicesData());
  }, []);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    let filteredServices = securityServicesData.services;
    let filteredFeatures = securityServicesData.features;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredServices = filteredServices.filter(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description.toLowerCase().includes(searchLower) ||
        service.category.toLowerCase().includes(searchLower)
      );
      filteredFeatures = filteredFeatures.filter(feature =>
        feature.name.toLowerCase().includes(searchLower) ||
        feature.description.toLowerCase().includes(searchLower) ||
        feature.category.toLowerCase().includes(searchLower)
      );
    }

    // Filter by service name (not category)
    if (selectedServiceCategory !== 'all') {
      filteredServices = filteredServices.filter(service =>
        service.name === selectedServiceCategory
      );
    }

    // Filter by feature category
    if (selectedFeatureCategory !== 'all') {
      filteredFeatures = filteredFeatures.filter(feature =>
        feature.category === selectedFeatureCategory
      );
    }

    return {
      services: filteredServices,
      features: filteredFeatures
    };
  }, [searchTerm, selectedServiceCategory, selectedFeatureCategory]);

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedServiceCategory('all');
    setSelectedFeatureCategory('all');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <SecurityServicesHeader />

      {/* Filters */}
      <SecurityServicesFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedServiceCategory={selectedServiceCategory}
        setSelectedServiceCategory={setSelectedServiceCategory}
        selectedFeatureCategory={selectedFeatureCategory}
        setSelectedFeatureCategory={setSelectedFeatureCategory}
        viewMode={viewMode}
        setViewMode={setViewMode}
        resetFilters={resetFilters}
        serviceCategories={securityServicesData.services.map(service => service.name)}
        featureCategories={securityServicesData.categories.features}
        totalServices={filteredData.services.length}
        totalFeatures={filteredData.features.length}
      />

      {/* Matrix Content */}
      <div className="flex-1 min-h-0">
        <SecurityServicesTable
          services={filteredData.services}
          features={filteredData.features}
          featureServices={securityServicesData.featureServices}
          viewMode={viewMode}
        />
      </div>
    </div>
  );
};

export default SecurityServicesMatrix; 